/**
 * StatusBadge - colored pill for a TicketStatus value.
 *
 * Audience note (May 2026): the same component renders on the customer
 * portal AND in the agent inbox. The label for ``waiting_customer``
 * must therefore flip with the viewer - "Waiting on you" reads
 * naturally to a customer but is actively misleading to an agent
 * looking at someone else's ticket. The ``viewer`` prop controls
 * which side reads it; default is ``"agent"`` so any new agent-side
 * caller picks up the safe-for-agents copy without thinking about
 * it, and the customer-side callers (MyTicketsPage's inline
 * TicketTableRow, TicketDetailCustomerPage, RecentlyViewedRail) opt
 * in to viewer="customer" explicitly.
 *
 * Every other status is audience-neutral and renders the same label
 * on both sides.
 */
import type { TicketStatus } from "@/types/api";

export type BadgeViewer = "agent" | "customer";

interface StatusInfo {
  /**
   * Label rendered to an agent looking at this ticket. This is the
   * default and the form used most places in the app.
   */
  agentLabel: string;
  /**
   * Label rendered to the customer who owns / sees this ticket. Set
   * only for statuses whose meaning shifts with the audience; falls
   * back to ``agentLabel`` when omitted.
   */
  customerLabel?: string;
  classes: string;
}

const STATUS_INFO: Record<TicketStatus, StatusInfo> = {
  open: {
    agentLabel: "Open",
    classes: "bg-blue-100 text-blue-700",
  },
  pending: {
    agentLabel: "Pending",
    classes: "bg-amber-100 text-amber-700",
  },
  waiting_customer: {
    // Agent reading the inbox: "this ticket is blocked on the
    // customer's response." Customer reading the portal: "we're
    // waiting on you to write back." Same row, different reader,
    // different sentence.
    agentLabel: "Waiting on customer",
    customerLabel: "Waiting on you",
    classes: "bg-amber-100 text-amber-700",
  },
  waiting_third_party: {
    agentLabel: "Waiting on third party",
    classes: "bg-amber-100 text-amber-700",
  },
  resolved: {
    agentLabel: "Resolved",
    classes: "bg-emerald-100 text-emerald-700",
  },
  closed: {
    agentLabel: "Closed",
    classes: "bg-neutral-200 text-neutral-700",
  },
  reopened: {
    agentLabel: "Reopened",
    classes: "bg-violet-100 text-violet-700",
  },
  spam: {
    agentLabel: "Spam",
    classes: "bg-red-100 text-red-700",
  },
  // Q1 resolved 2026-05-12: the three FD-custom statuses (8 / 9 / 10)
  // are no longer part of the v1 status vocabulary. The import folds
  // them at write time (see app/services/import_legacy/
  // freshdesk_import_service.py _STATUS_MAP). The UNKNOWN_INFO
  // fallback below is the last line of defense if a stale row ever
  // surfaces with one of those values.
};

// Defensive fallback for the case where the backend ships a status the
// frontend has not been rebuilt to recognize yet (the bug that bit us
// at the first FD import: imported tickets had `custom_8` and the map
// only had the eight v1 statuses). Surfacing the raw value in a
// neutral pill is preferable to crashing the whole inbox.
const UNKNOWN_INFO: StatusInfo = {
  agentLabel: "Unknown",
  classes: "bg-neutral-200 text-neutral-600",
};

// whitespace-nowrap: multi-word statuses ("Waiting on customer",
// "Waiting on third party") were wrapping inside narrow table cells
// and the side rail's Status card. Pills must stay on one line so
// the rounded shape and color band read as a single token.
const BASE_CLASSES =
  "inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium";

export interface StatusBadgeProps {
  status: TicketStatus;
  viewer?: BadgeViewer;
  className?: string;
}

export function StatusBadge({
  status,
  viewer = "agent",
  className = "",
}: StatusBadgeProps): JSX.Element {
  const info = STATUS_INFO[status] ?? {
    ...UNKNOWN_INFO,
    agentLabel: status ? String(status) : UNKNOWN_INFO.agentLabel,
  };
  const label =
    viewer === "customer" && info.customerLabel
      ? info.customerLabel
      : info.agentLabel;
  return (
    <span
      data-status={status}
      data-viewer={viewer}
      className={`${BASE_CLASSES} ${info.classes} ${className}`}
    >
      {label}
    </span>
  );
}

export default StatusBadge;
